
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems, Portal } from '@headlessui/react';
import SearchInput from '../../../common/SearchInput';
import UserAvatar from '../../../common/UserAvatar';
import { ContactOwner } from 'types/crm/contacts';

interface OwnerSearchDropdownProps {
  owners: ContactOwner[];
  currentOwnerUuid: string | null | undefined; // undefined = nothing chosen, null = explicitly "None"
  onOwnerChange: (owner: ContactOwner | null) => void;
  placeholder?: string;
  disabled?: boolean;
  allowUnassigned?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showChevron?: boolean;
  showName?: boolean;
  unassignedLabel?: string;
}

const sizeClasses = {
  sm: 'px-3 py-2 min-w-0',
  md: 'px-3 py-2 min-w-[100px]',
  lg: 'px-4 py-2.5 min-w-[120px]',
};

const avatarSizes = {
  sm: 'w-6 h-6 text-[10px] leading-none',
  md: 'w-7 h-7 text-xs leading-none',
  lg: 'w-8 h-8 text-sm leading-none',
};

const textSizes = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

const OwnerSearchDropdown: React.FC<OwnerSearchDropdownProps> = ({
  owners,
  currentOwnerUuid,
  onOwnerChange,
  placeholder = 'Unassigned',
  disabled = false,
  allowUnassigned = true,
  size = 'md',
  className = '',
  showChevron = true,
  showName = true,
  unassignedLabel = 'Unassigned',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const currentOwner = useMemo(
    () => owners.find((o) => o.uuid === currentOwnerUuid) ?? null,
    [owners, currentOwnerUuid]
  );

  const filteredOwners = useMemo(
    () =>
      owners.filter((o) => {
        const name = o.name.toLowerCase();
        return (
          name.includes(searchTerm.toLowerCase()) ||
          (o.email ? o.email.toLowerCase().includes(searchTerm.toLowerCase()) : false)
        );
      }),
    [owners, searchTerm]
  );

  useEffect(() => {
    if (open) setSearchTerm('');
  }, [open]);

  return (
    <Menu as="div" className="relative inline-block text-left w-full ">
      <MenuButton
        disabled={disabled}
        onClick={() => setOpen(true)}
        title={!showName && currentOwner ? currentOwner.name : undefined}
        className={`flex items-center ${showName ? 'gap-2.5 text-left' : 'justify-center'} w-full ${sizeClasses[size]} rounded hover:bg-primary-subtle transition disabled:opacity-50 focus:outline-none focus:ring-0 ${className}`}
      >
        {currentOwner ? (
          <>
            <UserAvatar name={currentOwner.name} profileImageURL={currentOwner.profileImageURL} className={avatarSizes[size]} />
            {showName && <span className={`${textSizes[size]} truncate flex-1 min-w-0`}>{currentOwner.name}</span>}
          </>
        ) : allowUnassigned && currentOwnerUuid === null ? (
          <>
            <div className={`rounded-full border border-dashed border-neutral-light/20 flex items-center justify-center text-neutral-medium ${avatarSizes[size]}`}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            {showName && <span className={`${textSizes[size]} truncate flex-1 min-w-0`}>{unassignedLabel}</span>}
          </>
        ) : (
          showName ? (
            <span className={`${textSizes[size]}  text-[#374151]  truncate`}>{placeholder}</span>
          ) : (
            <div className={`rounded-full border border-neutral-light/20 flex items-center justify-center text-neutral-medium ${avatarSizes[size]}`}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )
        )}
        {showChevron && (
          <ChevronDownIcon className={`${showName ? 'ml-auto' : ''} w-6 h-6 pr-2 shrink-0 text-neutral-medium`} />
        )}
      </MenuButton>

      <Portal>
        <MenuItems
          anchor="bottom start"
          className="mt-2 w-[250px]
          z-[9999]
          bg-primary-variant
          border border-primary-subtle
          rounded-lg shadow-xl
          focus:outline-none overflow-hidden"
        >
          {/* Search Header */}
          <div className="p-2 border-b border-primary-subtle bg-primary-variant">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search owners"
              className="bg-primary-subtle border-transparent"
              autoFocus
            />
          </div>

          <div className="max-h-[250px] overflow-y-auto py-1.5 px-1">
            {/* Unassigned Option */}
            {allowUnassigned && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={() => onOwnerChange(null)}
                    className={`w-full px-3 py-2 flex items-center gap-3 transition-colors ${focus ? 'bg-primary-subtle' : ''
                      }`}
                  >
                    <div className="w-7 h-7 rounded-full flex items-center shrink-0 justify-center text-neutral text-xs font-semibold bg-primary-subtle border border-primary-intermediate">
                      <svg className="w-3.5 h-3.5 text-neutral-medium" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="min-w-0 text-left flex flex-col justify-center">
                      <div className="truncate text-sm font-medium text-neutral leading-tight">
                        {unassignedLabel}
                      </div>
                    </div>
                  </button>
                )}
              </MenuItem>
            )}

            {/* Owner Options */}
            {filteredOwners.map((owner) => (
              <MenuItem key={owner.uuid}>
                {({ focus }) => (
                  <button
                    onClick={() => onOwnerChange(owner)}
                    className={`w-full px-3 py-2 flex items-center gap-3 transition-colors ${focus ? 'bg-primary-subtle' : ''
                      }`}
                  >
                    <UserAvatar
                      name={owner.name}
                      profileImageURL={owner.profileImageURL}
                      className="w-7 h-7 text-xs"
                    />
                    <div className="min-w-0 text-left flex flex-col justify-center">
                      <div className="truncate text-sm font-medium text-neutral leading-tight">
                        {owner.name}
                      </div>
                    </div>
                  </button>
                )}
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </Portal>
    </Menu>
  );
};

export default OwnerSearchDropdown;